import { ChangeDetectionStrategy, Component, HostListener, inject, Input, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import {
  FooterBaseComponent,
  Instance,
  InstanceResolveData,
  NavigationData,
  doArrayDirtyCheck,
  OverlayService,
} from '@finra/rampdi-forms-common';
import { CompletenessCheckService } from '../../services/completeness-check.service';
import { InstanceService, MU3WithSupportingData } from '../../services/instance.service';
import { RedlineService } from '../../services/redline.service';
import { BehaviorSubject, combineLatest, EMPTY, filter, map, Observable, Subject, takeUntil } from 'rxjs';
import { SharedStateService } from '../../services/shared-state.service';
import { CanAttestResponse, DisplayRulesService, FilingStatusDisplayRules } from '../../services/display-rules.service';
import { environment, Versions } from 'projects/app-mu3-form/src/environments/environment';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['section', 'lastOpenedSectionDE', 'saveInProgress', 'viewType'],
  outputs: ['submit', 'save', 'validate', 'next', 'back', 'print', 'redline', 'updateOptions'],
})
export class FooterComponent
  extends FooterBaseComponent<
    MU3WithSupportingData,
    Instance<any>,
    InstanceResolveData<MU3WithSupportingData, Instance<any>>
  >
  implements OnInit, OnDestroy
{
  @Input() isWebCRDDown;
  @Input() sectionNavData;
  autoSaveData$;
  displayRules$: Observable<FilingStatusDisplayRules> = inject(DisplayRulesService).displayRulesSubj;
  isDisableSub$ = new BehaviorSubject<boolean>(true);
  private destroySub$: Subject<void> = new Subject<void>();
  private destroyCCSub$: Subject<void> = new Subject<void>();
  navSelectedSection: string;
  redlineChecked = false;
  isInstanceSuccessfullyProcessed = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private OS: OverlayService,
    private CC: CompletenessCheckService,
    private RL: RedlineService,
    private IS: InstanceService,
    private DS: DisplayRulesService,
    private sharedStateService: SharedStateService,
  ) {
    super(router, route, OS, CC, RL, IS);
  }

  get isRegulatorUser(): boolean {
    return this.DS.isRegulatorUser.value;
  }

  ngOnInit() {
    this.completenessCheckHandler();
    combineLatest([
      this.DS.canAttestResp$ as Observable<CanAttestResponse>,
      this.sharedStateService.submittedWithoutPayment$,
    ])
      .pipe(takeUntil(this.destroySub$))
      .subscribe(([res, isSubmitted]) => {
        this.isDisableSub$.next(
          isSubmitted || !(res.attestChecked && !res.hasError && !res.isCompletenessCheckLoading),
        );
      });
    this.autoSaveData$ = this.IS.saveMetadata$;

    // Initialize the shared state service with the current instance status
    if (this.IS.currentInstance?.instanceMetadata?.status === 'SUBMITTED') {
      this.sharedStateService.setPostSubmission(true);
      this.isInstanceSuccessfullyProcessed = true;
    }

    this.route.queryParams.subscribe((params) => {
      if (params?.redlineCheck) {
        this.onToggleRedline(true);
        this.router.navigate([], {
          queryParams: { redlineCheck: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    });
  }

  completenessCheckHandler() {
    this.navSelectedSection =
      this.sectionNavData?.[0]?.children?.find((section) => section?.active)?.id ?? this.navSelectedSection;
    if (this.navSelectedSection === 'attestAndSubmit') {
      // to make sure whenever handler triggers it will check for cloudwell and remove it if its there
      delete this.IS.currentInstance.collectedData?.cloudwell;
      this.CC.setCompletenessCheckData({ close: true });
    }

    // Teardown previous CC subscription to prevent duplicate validate calls
    this.destroyCCSub$.next();

    this.CC.completenessCheck$
      .pipe(
        takeUntil(this.destroyCCSub$),
        takeUntil(this.destroySub$),
        map((ccResp) => {
          if (ccResp?.fail) {
            this.updateAttestState({
              hasError: true,
              isCompletenessCheckLoading: ccResp?.loading !== undefined ? !!ccResp.loading : true,
            });
            return EMPTY;
          }

          //Open CC only on Attest and Submit page
          if (this.navSelectedSection === 'attestAndSubmit' && ccResp?.close && !this.isInstanceProcessedOrFailed()) {
            // to make sure whenever handler triggers it will check for cloudwell and remove it if its there
            delete this.IS.currentInstance.collectedData?.cloudwell;
            this.onValidate();
            this.CC.setCompletenessCheckData({ open: true, loading: true });
          }

          const errorCount = ccResp?.count?.error ? ccResp?.count?.error : 0;
          //Check ccResp is successful
          const hasError = errorCount > 0 && !ccResp?.success;

          this.updateAttestState({
            hasError,
            isCompletenessCheckLoading:
              ccResp?.open === false ? false : ccResp?.loading !== undefined ? !!ccResp.loading : true,
            errorCount,
          });
        }),
      )
      .subscribe();
  }

  private updateAttestState(state: Partial<CanAttestResponse>) {
    this.DS.canAttestResp.next({
      ...this.DS.canAttestResp.getValue(),
      ...state,
    });
  }

  ngAfterViewInit(): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      let currentRoute = event.urlAfterRedirects;
      let attestAndSubmitRoute = currentRoute.includes('attestAndSubmit');
      this.DS.sentIsVerifiedData(false);
      if (attestAndSubmitRoute) {
        this.completenessCheckHandler();
      }
    });
  }

  isInstanceProcessedOrFailed() {
    // Use service method to get current value synchronously without creating subscription
    const currentRules = this.DS.getCurrentDisplayRules();
    const instanceStatus = currentRules?.displayedStatus;
    return instanceStatus === 'Filing Processed' || instanceStatus === 'Failed Processing';
  }

  saveData() {
    delete this.IS.currentInstance.collectedData?.cloudwell;
    this.onSave({ state: { beastNavEventKey: 'save' } });
  }

  /**
   * Override parent class method to handle redline toggle
   */
  onToggleRedline(checked: boolean) {
    this.redlineChecked = checked;

    if (checked) {
      this.redline.next({ value: true, toggleType: 'all' });
    } else {
      this.redline.next(false);
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    if (e.code === 'Enter' || e.code === 'Space') {
      if (e.target === document.getElementById('completeness-check-button')) {
        this.onValidate();
      } else if (e.target === document.getElementById('form-view-button')) {
        this.onFormView();
      } else if (!this.saveInProgress && e.target === document.getElementById('save-button')) {
        this.onSave({ state: { beastNavEventKey: 'save' } });
      } else if (e.target === document.getElementById('print-button')) {
        this.onPrint();
      } else if (e.target === document.getElementById('redline-slider')) {
        e.preventDefault();
        this.redlineChecked = !this.redlineChecked;
        this.onToggleRedline(this.redlineChecked);
      }
    }
  }

  onFormView() {
    if (this.viewType === 'formView') {
      return;
    }

    this.closeCC();
    this.updateLastOpenedSection();

    if (this.rampDoArrayDirtyCheck()) {
      this.OS.openStayLeaveModal()
        .afterClosed$.toPromise()
        .then((response) => {
          if (response.data) {
            this.openFormView('formViewIndividualInformation');
          }
        });
    } else {
      if (this.section !== 'attestAndSubmit' && this.section?.split(';')[0] !== 'guidance') {
        this.openFormView('formView' + this.section?.charAt(0)?.toUpperCase() + this.section?.slice(1));
      } else {
        const openView = this.section === 'attestAndSubmit' ? 'all' : 'formViewDocuments';
        this.openFormView(openView);
      }
    }
  }

  rampDoArrayDirtyCheck() {
    return doArrayDirtyCheck();
  }

  /**
   * Determines if the current section is the first navigable section.
   * Handles collapsible groups by flattening to leaf nodes.
   */
  isFirstSection(): boolean {
    const flatSections = this.getFlatNavigableSections();
    return flatSections.length > 0 && this.section === flatSections[0]?.id;
  }

  /**
   * Determines if the current section is the last navigable section.
   * Handles collapsible groups by flattening to leaf nodes.
   */
  isLastSection(): boolean {
    const flatSections = this.getFlatNavigableSections();
    return flatSections.length > 0 && this.section === flatSections[flatSections.length - 1]?.id;
  }

  /**
   * Flattens nav data to get ordered list of navigable (leaf) sections,
   * expanding collapsible groups into their children.
   * Result is cached per sectionNavData reference to avoid redundant computation on each CD cycle.
   */
  private cachedNavDataRef: any = null;
  private cachedFlatSections: NavigationData[] = [];

  private getFlatNavigableSections(): NavigationData[] {
    if (this.sectionNavData === this.cachedNavDataRef) {
      return this.cachedFlatSections;
    }
    this.cachedNavDataRef = this.sectionNavData;
    this.cachedFlatSections =
      this.sectionNavData?.[0]?.children?.reduce((acc: NavigationData[], item: NavigationData) => {
        if (!item.skip) {
          acc.push(item);
        }
        if (item.children?.length) {
          acc = [...acc, ...item.children.filter((i: NavigationData) => !i.skip)];
        }
        return acc;
      }, [] as NavigationData[]) ?? [];
    return this.cachedFlatSections;
  }

  /**
   * Function : To change Next and Previous Button names based on section
   * @returns : associated section names for buttons
   */
  footerButtonTextSetter(section: string, position: string) {
    this.navSelectedSection = section;
    let buttonText = '';
    const sectionNavData = this.sectionNavData?.[0]?.children?.reduce((acc, item) => {
      if (!item.skip) {
        acc.push(item);
      }
      if (item.children?.length) {
        acc = [...acc, ...item.children.filter((i) => !i.skip)];
      }
      return acc;
    }, []);
    sectionNavData?.forEach((child: any, index: number) => {
      if (section === child.id && position === 'next') {
        buttonText = sectionNavData[index + 1]?.id.includes('guidance')
          ? `Agency Guidance - ${sectionNavData[index + 1]?.text?.split(':')[0]}`
          : sectionNavData[index + 1]?.text;
        return;
      } else if (section === child.id && position === 'previous') {
        buttonText = sectionNavData[index - 1]?.id.includes('guidance')
          ? `Agency Guidance - ${sectionNavData[index - 1]?.text?.split(':')[0]}`
          : sectionNavData[index - 1]?.text;
        return;
      }
    });
    return buttonText;
  }

  retrieveTemplateInfoFromCurrentInstance(): {
    templateVersion;
    templateName;
    templateNamespace;
    id;
    currentEnvVersion;
  } {
    const { templateVersion, templateName, templateNamespace, id } = this.IS.currentInstance;
    return {
      templateVersion,
      templateName,
      templateNamespace,
      id,
      currentEnvVersion: environment.versions[(templateVersion ?? environment.currentVersion) as Versions],
    };
  }

  openTermsModal() {
    if (this.saveInProgress) return;
    // Set submitted state to disable button reactively (no payment modal in MU3)
    this.sharedStateService.setSubmittedWithoutPayment(true);
    // Payment/Invoice not implemented for MU3 — submit directly
    this.submit.next({});
  }

  ngOnDestroy(): void {
    this.destroyCCSub$.next();
    this.destroyCCSub$.complete();
    this.destroySub$.next();
    this.destroySub$.complete();
  }
}
